import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import { AvisoDeNovaVersao } from './components/NovaVersao.js';
import { FaixaDeAmbiente } from './components/FaixaDeAmbiente.js';
import { ApiError } from './api/client.js';
import { chaves } from './api/queries.js';
import './styles.css';

/**
 * Só em desenvolvimento (em build `import.meta.hot` não existe e o bloco some).
 *
 * Quando perde a conexão com o servidor de dev, o cliente do Vite espera o
 * servidor responder e recarrega a página. No celular a conexão cai toda vez que
 * o Android manda o navegador para segundo plano para abrir a câmera — e a página
 * recarregava justo quando a foto começava a subir, perdendo o cadastro.
 *
 * O cliente aguarda os ouvintes de `vite:ws:disconnect` antes de recarregar;
 * uma promessa que nunca termina cancela esse recarregamento. Vale só para tela
 * de toque: no PC, recarregar sozinho depois de reiniciar o servidor continua
 * útil. Ver docs/decisoes.md, "Cadastro resistente a recarga".
 */
if (import.meta.hot && window.matchMedia('(pointer: coarse)').matches) {
  import.meta.hot.on('vite:ws:disconnect', () => {
    console.info('[adega] conexão com o Vite perdida; recarga automática desligada no celular.');
    return new Promise<void>(() => {});
  });
}

/**
 * Sessão encerrada de fora — senha trocada em outro aparelho, ou cookie expirado:
 * a API passa a responder 401 UNAUTHENTICATED. Em vez de cada tela mostrar
 * "Faça login para continuar", reconsulta a sessão; o app vê que não está mais
 * autenticado e mostra o login.
 */
function aoFalhar(erro: unknown) {
  if (erro instanceof ApiError && erro.code === 'UNAUTHENTICATED') {
    void queryClient.invalidateQueries({ queryKey: chaves.auth });
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: aoFalhar }),
  mutationCache: new MutationCache({ onError: aoFalhar }),
  defaultOptions: {
    queries: {
      /**
       * Repetir só o que pode melhorar na segunda tentativa.
       *
       * `retry: 1` repetia tudo: 404 e 401 viravam duas viagens para dar a
       * mesma resposta, e prazo estourado dobrava a espera — trinta segundos
       * até a tela admitir que está sem conexão. Falha de rede seca (servidor
       * inalcançável) falha rápido e merece a segunda chance, porque costuma
       * ser oscilação de Wi-Fi.
       */
      retry: (tentativas, erro) => {
        if (erro instanceof ApiError && (erro.status >= 400 || erro.code === 'TIMEOUT')) {
          return false;
        }
        return tentativas < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
    },
  },
});

const raiz = document.getElementById('root');
if (!raiz) {
  throw new Error('Elemento #root não encontrado');
}

createRoot(raiz).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
        {/* Fora do App de proposito. E este componente que REGISTRA o service
            worker, e registro nao pode depender de login: do lado de dentro do
            portao, quem ainda nao entrou nao teria service worker nenhum e o
            navegador deixaria de oferecer a instalacao do PWA. Aqui ele monta
            uma vez, em qualquer tela, e fica invisivel enquanto nao ha versao
            nova. Dentro do QueryClientProvider porque consulta o estado da
            conexao para nao falar quando o servidor esta fora de alcance. */}
        <AvisoDeNovaVersao />
        <FaixaDeAmbiente />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
